import React, { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Loader2, MessageCircle, Send, PlayCircle, ShieldAlert, ShieldCheck } from "lucide-react";
import { fechaHoraCompleta } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const MODOS = [
  { v: "apagado", get l() { return t("Apagado — no envía nada"); } },
  { v: "simulacion", get l() { return t("Simulación — registra el intento, no envía"); } },
  { v: "real", get l() { return t("Real — envía por WhatsApp"); } },
];

const ESTADO_ENVIO_LABEL = {
  get enviado() { return t("Enviado"); },
  get simulado() { return t("Simulado"); },
  get omitido() { return t("Omitido"); },
  get error() { return t("Error"); },
  get duplicado() { return t("Duplicado"); },
};

export default function WhatsAppConfig() {
  const [conf, setConf] = useState(null);
  const [envios, setEnvios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [telefonoPrueba, setTelefonoPrueba] = useState("");
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);
  const [ejecutando, setEjecutando] = useState("");

  const cargar = useCallback(async () => {
    const [c, e] = await Promise.all([api.get("/whatsapp/config"), api.get("/whatsapp/envios")]);
    setConf(c.data);
    setEnvios(e.data);
  }, []);

  useEffect(() => { cargar().catch(() => {}); }, [cargar]);

  const setCampo = (campo, valor) => setConf((c) => ({ ...c, [campo]: valor }));

  const guardar = async () => {
    setSaving(true);
    try {
      const { credenciales_ok, ...payload } = conf;
      const { data } = await api.put("/whatsapp/config", payload);
      setConf(data);
      toast.success(t("Configuración de WhatsApp guardada"));
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const enviarPrueba = async () => {
    if (!telefonoPrueba.trim()) return toast.error(t("Escribe un teléfono"));
    setEnviandoPrueba(true);
    try {
      const { data } = await api.post("/whatsapp/prueba", { telefono: telefonoPrueba.trim() });
      toast.success(t("Resultado: {v0}{v1}", { v0: ESTADO_ENVIO_LABEL[data.estado] || data.estado, v1: data.detalle ? ` — ${data.detalle}` : "" }));
      await cargar();
    } catch (e) { toast.error(apiError(e)); } finally { setEnviandoPrueba(false); }
  };

  const ejecutarAhora = async (tipo) => {
    setEjecutando(tipo);
    try {
      const { data } = await api.post("/whatsapp/ejecutar", { tipo });
      toast.success(t("Enviados {v0} · Simulados {v1} · Omitidos {v2} · Errores {v3}", { v0: data.enviado || 0, v1: data.simulado || 0, v2: data.omitido || 0, v3: data.error || 0 }));
      await cargar();
    } catch (e) { toast.error(apiError(e)); } finally { setEjecutando(""); }
  };

  if (!conf) return <div className="p-6"><Loader2 className="h-6 w-6 animate-spin text-ink-3" /></div>;

  return (
    <div className="space-y-6">
      <div className={cn("rounded-xl border-2 p-4 flex items-start gap-3", conf.credenciales_ok ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50")} data-testid="whatsapp-credenciales-status">
        {conf.credenciales_ok ? <ShieldCheck className="h-5 w-5 text-[#277A3F] shrink-0 mt-0.5" /> : <ShieldAlert className="h-5 w-5 text-[#B07A12] shrink-0 mt-0.5" />}
        <div>
          <p className={cn("text-sm font-bold", conf.credenciales_ok ? "text-emerald-800" : "text-amber-800")}>
            {conf.credenciales_ok ? t("Credenciales de Meta configuradas") : t("Todavía no hay credenciales de Meta (WHATSAPP_TOKEN / WHATSAPP_PHONE_NUMBER_ID)")}
          </p>
          <p className="text-xs text-ink-2 mt-0.5">
            {conf.credenciales_ok
              ? t("El modo \"real\" puede enviar mensajes por WhatsApp.")
              : t("Mientras no estén, el modo \"real\" quedará sin efecto (los envíos se marcan como error). Usa \"simulación\" para probar el flujo sin credenciales.")}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6 space-y-5">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-ink" />
          <h2 className="font-display font-semibold text-[18px] text-ink">{t("Configuración de WhatsApp")}</h2>
        </div>

        <div>
          <Label className="eyebrow">{t("Modo")}</Label>
          <Select value={conf.whatsapp_modo} onValueChange={(v) => setCampo("whatsapp_modo", v)}>
            <SelectTrigger data-testid="whatsapp-modo-select" className="h-11 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {MODOS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="eyebrow">{t("Hora del recordatorio (mañana)")}</Label>
            <Input data-testid="whatsapp-hora-recordatorio" type="time" value={conf.whatsapp_hora_recordatorio} onChange={(e) => setCampo("whatsapp_hora_recordatorio", e.target.value)} className="mt-1 h-11" />
          </div>
          <div>
            <Label className="eyebrow">{t("Hora del aviso de papeles pendientes")}</Label>
            <Input data-testid="whatsapp-hora-pendientes" type="time" value={conf.whatsapp_hora_pendientes} onChange={(e) => setCampo("whatsapp_hora_pendientes", e.target.value)} className="mt-1 h-11" />
          </div>
          <div>
            <Label className="eyebrow">{t("Plantilla — recordatorio de turno")}</Label>
            <Input data-testid="whatsapp-plantilla-recordatorio" value={conf.whatsapp_plantilla_recordatorio} onChange={(e) => setCampo("whatsapp_plantilla_recordatorio", e.target.value)} className="mt-1 h-11 font-mono" />
          </div>
          <div>
            <Label className="eyebrow">{t("Plantilla — papeles pendientes")}</Label>
            <Input data-testid="whatsapp-plantilla-pendientes" value={conf.whatsapp_plantilla_pendientes} onChange={(e) => setCampo("whatsapp_plantilla_pendientes", e.target.value)} className="mt-1 h-11 font-mono" />
          </div>
          <div>
            <Label className="eyebrow">{t("Idioma de la plantilla (Meta)")}</Label>
            <Input data-testid="whatsapp-idioma" value={conf.whatsapp_idioma} onChange={(e) => setCampo("whatsapp_idioma", e.target.value)} className="mt-1 h-11 font-mono" />
          </div>
        </div>

        <Button data-testid="whatsapp-guardar" onClick={guardar} disabled={saving} className="h-11 bg-brand hover:bg-brand-dark text-white px-8">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar")}
        </Button>
      </div>

      <div className="bg-white rounded-xl border p-6 space-y-4">
        <h2 className="font-display font-semibold text-[18px] text-ink">{t("Probar y ejecutar")}</h2>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <Label className="eyebrow">{t("Teléfono de prueba")}</Label>
            <Input data-testid="whatsapp-telefono-prueba" placeholder={t("+1XXXXXXXXXX")} value={telefonoPrueba} onChange={(e) => setTelefonoPrueba(e.target.value)} className="mt-1 h-11" />
          </div>
          <Button data-testid="whatsapp-enviar-prueba" variant="outline" className="h-11" onClick={enviarPrueba} disabled={enviandoPrueba}>
            {enviandoPrueba ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />{t(" Enviar prueba")}</>}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="whatsapp-ejecutar-recordatorio" variant="outline" className="h-10" onClick={() => ejecutarAhora("recordatorio")} disabled={!!ejecutando}>
            {ejecutando === "recordatorio" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4 mr-2" />{t(" Ejecutar recordatorios ahora")}</>}
          </Button>
          <Button data-testid="whatsapp-ejecutar-pendientes" variant="outline" className="h-10" onClick={() => ejecutarAhora("pendientes")} disabled={!!ejecutando}>
            {ejecutando === "pendientes" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4 mr-2" />{t(" Ejecutar avisos de pendientes ahora")}</>}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6">
        <h2 className="font-display font-semibold text-[18px] text-ink mb-4">{t("Historial de envíos")}</h2>
        <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
          <table className="w-full text-sm" data-testid="whatsapp-envios-tabla">
            <thead className="bg-paper sticky top-0">
              <tr className="text-left eyebrow">
                <th className="px-3 py-2">{t("Tipo")}</th>
                <th className="px-3 py-2">{t("Chofer")}</th>
                <th className="px-3 py-2">{t("Teléfono")}</th>
                <th className="px-3 py-2">{t("Estado")}</th>
                <th className="px-3 py-2">{t("Detalle")}</th>
                <th className="px-3 py-2">{t("Fecha")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {envios.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-3">{t("Sin envíos todavía")}</td></tr>}
              {envios.map((e) => (
                <tr key={e.id} data-testid={`whatsapp-envio-${e.id}`}>
                  <td className="px-3 py-2 capitalize">{e.tipo}</td>
                  <td className="px-3 py-2">{e.chofer_nombre || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{e.telefono || "—"}</td>
                  <td className="px-3 py-2 font-medium">{ESTADO_ENVIO_LABEL[e.estado] || e.estado}</td>
                  <td className="px-3 py-2 text-ink-3">{e.detalle || "—"}</td>
                  <td className="px-3 py-2 text-ink-3">{fechaHoraCompleta(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
